import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { setStepUpRequester, setStepUpToken } from "@/lib/stepup";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck } from "@phosphor-icons/react";

const codeInput = "h-12 w-full rounded-[10px] border border-white/10 bg-black/40 px-3 text-center text-[18px] tracking-[0.2em] text-[#F6F5F1] focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

// Global provider: registers a step-up requester so the axios interceptor can pop
// this dialog whenever a destructive admin action returns 428. On success it stores
// a fresh step-up token and resolves, letting the original request retry.
export function StepUpProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const resolveRef = useRef(null);
  const rejectRef = useRef(null);

  useEffect(() => {
    setStepUpRequester(() => new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
      setCode(""); setError(""); setOpen(true);
    }));
  }, []);

  const finish = (ok) => {
    setOpen(false);
    if (ok) { resolveRef.current && resolveRef.current(); }
    else { rejectRef.current && rejectRef.current(new Error("cancelled")); }
    resolveRef.current = null; rejectRef.current = null;
  };

  const submit = async () => {
    const c = code.trim();
    if (!c) return;
    setBusy(true); setError("");
    try {
      const { data } = await client.post("/auth/step-up", { code: c });
      setStepUpToken(data.step_up_token);
      setBusy(false);
      finish(true);
    } catch (e) {
      setBusy(false);
      setError(e.response?.data?.detail || "That code isn't right. Try again.");
    }
  };

  return (
    <>
      {children}
      <Dialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
        <DialogContent className="border-gold/25 bg-elevated text-white sm:max-w-sm" data-testid="step-up-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-xl text-[#F6F5F1]">
              <ShieldCheck size={20} weight="fill" className="text-gold" /> Confirm it's you
            </DialogTitle>
            <DialogDescription className="text-dim">
              This is a sensitive admin action. Enter a code from your authenticator app (or a backup code) to continue.
            </DialogDescription>
          </DialogHeader>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            inputMode="text" autoFocus placeholder="123456"
            data-testid="step-up-code-input" className={codeInput}
          />
          {error && <p className="text-[13px] text-red-300" data-testid="step-up-error">{error}</p>}
          <button onClick={submit} disabled={busy || !code.trim()} data-testid="step-up-confirm"
            className="btn-gold w-full rounded-full py-2.5 text-[14px] font-bold disabled:opacity-40">
            {busy ? "Verifying…" : "Confirm & continue"}
          </button>
          <button onClick={() => finish(false)} data-testid="step-up-cancel"
            className="w-full rounded-full py-2 text-[13px] font-semibold text-dim hover:text-[#F6F5F1]">
            Cancel
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
